import React, { useState, useMemo, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import * as adminService from "@/shared/api/services/adminService";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetail from "@/shared/components/transactions/TransactionDetail";
import { exportToCsv } from "@/shared/utils/csvExport";
import { useLanguage } from "@/shared/components/shared/LanguageContext";
import { ChevronLeft, ChevronRight, ArrowUpFromLine, CheckCircle2, Clock, Hash } from "lucide-react";

/* ── CSS — zero animations, pure CSS transitions, hardcoded hex ── */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@400;500;600;700;800&display=swap');

  .wd { display:flex; flex-direction:column; gap:1.25rem; background:#060C18; min-height:100%; padding:1.25rem; font-family:'Outfit',system-ui,sans-serif; }

  /* header */
  .wd-hdr { display:flex; flex-wrap:wrap; align-items:center; justify-content:space-between; gap:0.875rem; }
  .wd-hdr-l { display:flex; align-items:center; gap:0.75rem; }
  .wd-badge { width:2.5rem; height:2.5rem; border-radius:0.75rem; background:rgba(251,191,36,0.10); border:1px solid rgba(251,191,36,0.22); display:flex; align-items:center; justify-content:center; flex-shrink:0; }
  .wd-badge svg { color:#FBBF24; width:1.1rem; height:1.1rem; }
  .wd-title { font-size:1.375rem; font-weight:800; color:#EFF6FF; letter-spacing:-0.02em; margin:0; }
  .wd-sub { font-size:0.775rem; color:#4D6E90; margin-top:0.1rem; }
  .wd-sub b { color:#93B4D4; font-weight:600; }

  /* stat pills */
  .wd-pills { display:flex; gap:0.5rem; flex-wrap:wrap; }
  .wd-pill { display:inline-flex; align-items:center; gap:0.375rem; padding:0.35rem 0.75rem; border-radius:0.5rem; border:1px solid transparent; font-size:0.775rem; font-weight:600; white-space:nowrap; cursor:default; transition:background 120ms ease, border-color 120ms ease; }
  .wd-pill svg { width:0.75rem; height:0.75rem; }
  .wd-pill-c { background:rgba(34,211,238,0.08); border-color:rgba(34,211,238,0.18); color:#22D3EE; }
  .wd-pill-c:hover { background:rgba(34,211,238,0.13); border-color:rgba(34,211,238,0.30); }
  .wd-pill-s { background:rgba(16,185,129,0.08); border-color:rgba(16,185,129,0.18); color:#34D399; }
  .wd-pill-s:hover { background:rgba(16,185,129,0.13); border-color:rgba(16,185,129,0.30); }
  .wd-pill-p { background:rgba(251,191,36,0.08); border-color:rgba(251,191,36,0.18); color:#FBBF24; }
  .wd-pill-p:hover { background:rgba(251,191,36,0.13); border-color:rgba(251,191,36,0.30); }

  /* divider */
  .wd-div { height:1px; background:linear-gradient(90deg,transparent,rgba(34,211,238,0.22) 30%,rgba(251,191,36,0.14) 70%,transparent); border:none; margin:0; }

  /* filter section */
  .wd-filters { background:#0E1C35; border:1px solid rgba(255,255,255,0.07); border-radius:0.875rem; overflow:hidden; transition:border-color 150ms ease; }
  .wd-filters:hover { border-color:rgba(34,211,238,0.16); }

  /* table card */
  .wd-table { background:#0E1C35; border:1px solid rgba(255,255,255,0.07); border-radius:0.875rem; overflow:hidden; transition:border-color 150ms ease; }
  .wd-table:hover { border-color:rgba(34,211,238,0.14); }
  .wd-table ::-webkit-scrollbar { height:4px; width:4px; }
  .wd-table ::-webkit-scrollbar-track { background:transparent; }
  .wd-table ::-webkit-scrollbar-thumb { background:rgba(34,211,238,0.18); border-radius:99px; }

  /* pagination */
  .wd-pag { display:flex; align-items:center; justify-content:space-between; background:#0E1C35; border:1px solid rgba(255,255,255,0.07); border-radius:0.75rem; padding:0.55rem 0.875rem; }
  .wd-pag-info { font-size:0.775rem; color:#4D6E90; }
  .wd-pag-ctrl { display:flex; align-items:center; gap:0.4rem; }
  .wd-pag-lbl { font-size:0.775rem; color:#93B4D4; padding:0 0.2rem; }
  .wd-pag-btn { width:1.875rem; height:1.875rem; border-radius:0.45rem; display:flex; align-items:center; justify-content:center; background:#122040; border:1px solid rgba(255,255,255,0.09); color:#93B4D4; cursor:pointer; transition:background 100ms ease, border-color 100ms ease, color 100ms ease; }
  .wd-pag-btn svg { width:0.825rem; height:0.825rem; }
  .wd-pag-btn:hover:not(:disabled) { background:rgba(34,211,238,0.10); border-color:rgba(34,211,238,0.35); color:#22D3EE; }
  .wd-pag-btn:disabled { opacity:0.28; cursor:not-allowed; }
`;

const PAGE_SIZE = 20;
const RESET = {
  search: "",
  type: "all",
  status: "all",
  merchant: "all",
  provider: "all",
  currency: "all",
  location: "all",
  dateFrom: undefined,
  dateTo: undefined
};
const CSV_HEADERS = ["id", "transaction_id", "payment_id", "amount", "status", "currency", "merchant_id", "merchant_name", "bank_code", "bank_account", "account_name", "customer_name", "client_bank_name", "network", "wallet_address", "createdAt", "updatedAt", "type"];

export default function Withdrawals() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    status: "all",
    merchant: "all",
    provider: "all",
    currency: "all",
    location: "all",
    dateFrom: undefined,
    dateTo: undefined,
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  const filtersMapper = React.useMemo(() => ({
    status: filters.status && filters.status !== "all" ? filters.status : undefined,
    location: filters.location && filters.location !== "all" ? filters.location.toUpperCase() : undefined,
    currency: filters.currency && filters.currency !== "all" ? filters.currency.toUpperCase() : undefined,
    startDate: filters.dateFrom ? new Date(filters.dateFrom).toISOString() : undefined,
    endDate: filters.dateTo ? new Date(filters.dateTo).toISOString() : undefined,
  }), [filters]);

  const merchantIds = filters.merchant && filters.merchant !== "all" ? [filters.merchant] : null;

  const { data: withdrawalsData, isLoading: withdrawalsLoading } = useQuery({
    queryKey: ["all-withdrawals", page, pageSize, filters.search, filtersMapper, merchantIds],
    queryFn: () => adminService.getWithdrawalsPagination(page + 1, pageSize, filters.search, filtersMapper, merchantIds),
  });


  const withdrawals = withdrawalsData?.data || [];

  // Merchants for filters and lookup
  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: () => adminService.getAllMerchants(),
  });

  // Normalize withdrawals to table/detail shape
  const transactions = React.useMemo(() => {
    return withdrawals.map((w) => {
      const merchant = w.merchants || merchants.find((m) => m.id === w.merchant_id) || null;
      return {
        id: w.id,
        transaction_id: w.id,
        payment_id: w.id,
        reference: "",
        amount: Number(w.amount ?? 0),
        net_amount: Number(w.net_amount ?? 0),
        total_fee: Number(w.total_fee ?? 0),
        status: (w.status ?? "pending").toLowerCase(),
        currency: "THB",
        merchant_id: w.merchant_id,
        merchant,
        bank_code: w.bank_code,
        bank_account: w.bank_account,
        account_name: w.account_name,
        customer_name: w.account_name,
        client_bank_name: w.bank_name || w.bank_code,
        network: w.network,
        wallet_address: w.wallet_address,
        created_date: w.createdAt,
        updated_date: w.updatedAt ?? "",
        type: "WITHDRAWAL",
        _raw: w,
      };
    });
  }, [withdrawals, merchants]);

  const paginatedTransactions = transactions;
  const totalPages = withdrawalsData?.totalPages || 1;

  const totalTransactions = withdrawalsData?.total || 0;

  const stats = React.useMemo(() => {
    const success = transactions.filter((t) => t.status === "success");
    const pending = transactions.filter((t) => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((sum, t) => sum + (t.amount || 0), 0),
      count: totalTransactions,
      successCount: success.length,
      pendingCount: pending.length,
    };
  }, [transactions, totalTransactions]);

  const handleFilter = useCallback((f) => { setFilters(f); setPage(0); }, []);
  const handleReset = useCallback(() => { setFilters(RESET); setPage(0); }, []);
  const handleExport = useCallback(() => {
    exportToCsv(transactions, { headers: CSV_HEADERS, filename: `withdrawals-${new Date().toISOString().slice(0, 10)}.csv`, getCellValue: (r, k) => k === "merchant_name" ? r.merchant?.name : r[k] });
  }, [transactions]);

  return (
    <>
      <style>{CSS}</style>
      <div className="max-w-[1440px] mx-auto md:p-8 flex flex-col gap-6">

        {/* Header */}
        <div className="wd-hdr">
          <div className="wd-hdr-l">
            <div className="wd-badge"><ArrowUpFromLine /></div>
            <div>
              <h1 className="wd-title">{t("withdrawals")}</h1>
              <p className="wd-sub"><b>{stats.count}</b> {t("withdrawals")}</p>
            </div>
          </div>
          <div className="wd-pills">
            <span className="wd-pill wd-pill-c"><Hash />{stats.count} {t("total") || "Total"}</span>
            <span className="wd-pill wd-pill-s"><CheckCircle2 />{stats.successCount} {t("successful")}</span>
            <span className="wd-pill wd-pill-p"><Clock />{stats.pendingCount} {t("pending")}</span>
          </div>
        </div>

        <hr className="wd-div" />

        {/* Filters */}
        <div className="wd-filters">
          <TransactionFilters filters={filters} onFilterChange={handleFilter} merchants={merchants} providers={[]} onExport={handleExport} onReset={handleReset} />
        </div>

        {/* Table */}
        <div className="wd-table">
          <TransactionTable transactions={paginatedTransactions} isLoading={withdrawalsLoading} onView={setSelectedTransaction} isQRCode={false} mixedTypes={false} />
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="wd-pag">
            <span className="wd-pag-info">{page * PAGE_SIZE + 1}–{Math.min((page + 1) * PAGE_SIZE, totalTransactions)} of {totalTransactions}</span>
            <div className="wd-pag-ctrl">
              <button className="wd-pag-btn" onClick={() => setPage(page - 1)} disabled={page === 0}><ChevronLeft /></button>
              <span className="wd-pag-lbl">{page + 1} / {totalPages}</span>
              <button className="wd-pag-btn" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}><ChevronRight /></button>
            </div>
          </div>
        )}

        {/* Detail */}
        <TransactionDetail transaction={selectedTransaction} open={!!selectedTransaction} onClose={() => setSelectedTransaction(null)} isQRCode={false} />
      </div>
    </>
  );
}